<template>
  <Teleport to="#v-dialogs-container">
    <Transition v-if="!lazy || rendered" name="v-fade" @after-leave="$emit('afterLeave', $event)">
      <div
        v-show="isWrapperVisible"
        v-bind="$attrs"
        ref="wrapper"
        class="v-dialog-wrapper"
        :class="[`v-dialog-wrapper--${placement}`, { 'v-dialog-wrapper--hidden': hideWrapper || !isVisible }]"
        :style="{ zIndex }"
        @mousedown.self="clickOutside"
      >
        <Transition :name="_transition" @after-leave="hide">
          <div v-show="isVisible" :class="className" :style>
            <VLoadingDots v-if="loading" mask />

            <VIcon v-if="!hideCloseIcon" background-type="white" class="v-dialog__close" icon="Close" :size="22" @click="close" />

            <div class="v-dialog__main">
              <div v-if="headerVisible" class="v-dialog__header">
                <slot name="title">
                  <span class="v-dialog__title">{{ title }}</span>
                </slot>

                <div class="v-dialog__header-left">
                  <slot name="header-left" />
                </div>

                <div class="v-dialog__header-right">
                  <slot name="header-right" />
                </div>
              </div>

              <div class="v-dialog__body">
                <slot />
              </div>

              <div v-if="$slots.footer" class="v-dialog__footer">
                <slot name="footer" />
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onUnmounted, provide, ref, useTemplateRef, watch } from 'vue'

import { useClassName } from '../composables/useClassName.ts'
import { type Dialog, useDialogs } from '../composables/useDialogs.ts'
import type { VDialogPlacement } from '../types.ts'
import { configVDialog as config } from '../utils/config.ts'
import { findAndSpliceArray, nextFrame } from '../utils/helpers.ts'
import { $dialogKey } from '../utils/injections.ts'
import VIcon from './Icon/VIcon.vue'
import VLoadingDots from './VLoadingDots.vue'

interface Props {
  loading?: boolean
  hideCloseIcon?: boolean
  hideWrapper?: boolean
  closeOnClickOutside?: boolean
  lazy?: boolean
  placement?: VDialogPlacement
  title?: string
  top?: string
  width?: string
  transition?: string
  closeOnPressEscape?: boolean
  closeOnRouteChange?: boolean
  index?: number
}

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  hideCloseIcon: false,
  hideWrapper: false,
  closeOnClickOutside: undefined,
  lazy: true,
  placement: 'center',
  title: '',
  top: '15vh',
  width: '',
  transition: undefined,
  closeOnPressEscape: true,
  closeOnRouteChange: true,
  index: undefined,
})

const visible = defineModel<boolean>('visible', { default: true })

defineEmits<{
  afterLeave: [el: Element]
}>()

const slots = defineSlots<{
  default?(): any
  title?(): any
  footer?(): any
  'header-left'?: () => any
  'header-right'?: () => any
}>()

const { visibleDialogs, placementToTransition, counter } = useDialogs()
const rendered = ref(false)
const isVisible = ref(false)
const isWrapperVisible = ref(false)
const wrapper = useTemplateRef('wrapper')

const zIndex = computed(() => (props.index !== undefined ? props.index : visibleDialogs.findIndex((item) => item.id === dialog.id)))
const headerVisible = computed(() => props.title || slots.title || slots['header-left'] || slots['header-right'])
const _transition = computed(() => (props.transition !== undefined ? props.transition : placementToTransition[props.placement]))

const className = useClassName(
  'v-dialog',
  computed(() => [props.title]),
  computed(() => `v-dialog--${props.placement}`),
)
const style = computed(() => ({
  marginTop: props.placement === 'center' ? props.top : '',
  maxHeight: props.placement === 'center' ? `calc(100vh - ${props.top} - 50px)` : '100vh',
  width: props.placement === 'fullscreen' ? '100%' : props.width ? props.width : props.placement === 'bottom' || props.placement === 'top' ? '100%' : '50%',
}))

const dialog: Dialog = {
  id: counter.value++,
  props,
  wrapper,
  close,
}

watch(
  visible,
  async () => {
    if (visible.value) {
      rendered.value = true
      isWrapperVisible.value = true
      visibleDialogs.push(dialog)
    } else {
      findAndSpliceArray(visibleDialogs, (item) => item.id === dialog.id)
    }

    await nextFrame()
    isVisible.value = visible.value
  },
  { immediate: true },
)

onUnmounted(() => {
  findAndSpliceArray(visibleDialogs, (item) => item.id === dialog.id)
})

function clickOutside() {
  if (props.closeOnClickOutside === true || (props.closeOnClickOutside === undefined && config.closeOnClickOutside)) {
    close()
  }
}

function close() {
  isVisible.value = false
}

function hide() {
  isWrapperVisible.value = false
  visible.value = false
}

provide($dialogKey, {
  visible,
  isWrapperVisible,
})

defineExpose({
  clickOutside,
  close,
  hide,
  isVisible,
  isWrapperVisible,
})
</script>

<style>
.v-dialog-wrapper {
  position: fixed;
  overflow: hidden;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition:
    background-color var(--transition),
    opacity var(--transition);
  background-color: rgba(0, 0, 0, 0);
}

.v-dialog-wrapper:not(.v-dialog-wrapper--fullscreen, .v-dialog-wrapper--hidden) {
  background-color: rgba(0, 0, 0, 0.5);
}

.v-dialog-wrapper.v-dialog-wrapper--hidden {
  pointer-events: none;
}

.v-dialog {
  position: relative;
  margin: 0 auto 50px;
  padding: 60px;
  background: #fff;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-y: overlay;
  display: flex;
  flex-direction: column;
  max-width: 100vw;
  pointer-events: all;
}

.v-dialog.v-dialog--right {
  margin-right: 0;
  height: 100%;
}

.v-dialog.v-dialog--left {
  margin-left: 0;
  height: 100%;
}

.v-dialog.v-dialog--bottom {
  margin-bottom: 0;
  bottom: 0;
  position: absolute;
}

.v-dialog.v-dialog--top {
  margin-top: 0;
}

.v-dialog.v-dialog--fullscreen {
  height: 100%;
  padding-top: 30px;
}

.v-dialog.v-dialog--fullscreen .v-dialog__close {
  position: fixed;
}

.v-dialog.v-dialog--fullscreen .v-dialog__main {
  max-width: var(--max-main-width);
  margin: 0 auto;
  width: 100%;
}

.v-dialog--center {
  border-radius: 10px;
  box-shadow: var(--box-shadow);
  padding: 20px;
}

.v-dialog--center .v-dialog__title {
  font-size: 25px;
}

.v-dialog--center .v-dialog__footer {
  margin-bottom: 0;
}

.v-dialog__header {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.v-dialog__header .v-dialog__header-left {
  padding-left: 20px;
  flex-grow: 1;
}

.v-dialog__header .v-dialog__header-right {
  display: flex;
}

.v-dialog__title {
  font-size: 30px;
  font-weight: 500;
  line-height: 1;
}

.v-dialog__body {
  padding-bottom: 20px;
}

.v-dialog__close {
  top: 10px;
  right: 10px;
  position: absolute;
  cursor: pointer;
  transition: opacity var(--transition);
  z-index: 1;
}

.v-dialog__close:hover {
  opacity: 0.5;
}

.v-dialog__footer {
  text-align: right;
  position: relative;
  margin-top: auto;
  margin-bottom: -20px;
}

@media (max-width: 768px) {
  .v-dialog {
    padding: 20px;
    padding-bottom: 40px;
  }

  .v-dialog--center {
    min-width: 90vw;
  }

  .v-dialog--left,
  .v-dialog--right {
    min-width: 100%;
  }
}

@media (min-width: 768px) {
  .v-dialog--left,
  .v-dialog--right {
    min-width: 720px;
  }
}
</style>
